import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import {
  getActiveCountryCode,
  getIdentityMessageIds,
  isCustomerIdentityValid,
  isOfficialStoreIdentityValid,
  normalizeIdentityValue,
  normalizeOfficialStoreIdentityValue,
} from '../../../util/identity';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './ConfirmSignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const CuitMaybe = props => {
  const { formId, userType, intl, className } = props;
  const countryCode = getActiveCountryCode();

  if (userType !== 'tienda-oficial') {
    return null;
  }

  const identityMessages = getIdentityMessageIds('ConfirmSignupForm', userType, countryCode);
  const isBrazilCnpj = countryCode === 'BR';

  const cuitRequiredMessage = intl.formatMessage({ id: identityMessages.required });
  const cuitInvalidMessage = intl.formatMessage({ id: identityMessages.invalid });

  const cuitFormatValid = message => value => {
    const stringValue = typeof value === 'string' ? value : String(value || '');
    if (!stringValue.trim()) {
      return undefined;
    }
    return isOfficialStoreIdentityValid(value, countryCode) ? undefined : message;
  };

  const sanitizeCuitInput = value => normalizeOfficialStoreIdentityValue(value, countryCode);

  const handleCuitKeyDown = e => {
    const { key, metaKey, ctrlKey } = e;

    if (metaKey || ctrlKey || key.length > 1) {
      return;
    }

    const cuitAllowedPattern = isBrazilCnpj ? /[A-Za-z0-9]/ : /[0-9]/;
    if (!cuitAllowedPattern.test(key)) {
      e.preventDefault();
    }
  };

  const cuitValidation = validators.composeValidators(
    validators.required(cuitRequiredMessage),
    cuitFormatValid(cuitInvalidMessage)
  );

  return (
    <FieldTextInput
      className={className}
      type="text"
      name="cuit"
      id={formId ? `${formId}.cuit` : 'cuit'}
      inputMode={isBrazilCnpj ? 'text' : 'numeric'}
      pattern={isBrazilCnpj ? '[A-Za-z0-9]*' : '[0-9]*'}
      parse={sanitizeCuitInput}
      onKeyDown={handleCuitKeyDown}
      label={intl.formatMessage({ id: identityMessages.label })}
      placeholder={intl.formatMessage({ id: identityMessages.placeholder })}
      validate={cuitValidation}
    />
  );
};

const DniMaybe = props => {
  const { formId, userType, intl, className } = props;
  const countryCode = getActiveCountryCode();

  if (userType !== 'cliente') {
    return null;
  }

  const identityMessages = getIdentityMessageIds('ConfirmSignupForm', userType, countryCode);

  const dniRequiredMessage = intl.formatMessage({ id: identityMessages.required });
  const dniInvalidMessage = intl.formatMessage({ id: identityMessages.invalid });

  const dniFormatValid = message => value => {
    const stringValue = typeof value === 'string' ? value : String(value || '');
    if (!stringValue.trim()) {
      return undefined;
    }
    return isCustomerIdentityValid(value, countryCode) ? undefined : message;
  };

  const sanitizeDniInput = value => normalizeIdentityValue(value);

  const handleDniKeyDown = e => {
    const { key, metaKey, ctrlKey } = e;

    if (metaKey || ctrlKey || key.length > 1) {
      return;
    }

    if (!/[0-9]/.test(key)) {
      e.preventDefault();
    }
  };

  const dniValidation = validators.composeValidators(
    validators.required(dniRequiredMessage),
    dniFormatValid(dniInvalidMessage)
  );

  return (
    <FieldTextInput
      className={className}
      type="text"
      name="dni"
      id={formId ? `${formId}.dni` : 'dni'}
      inputMode="numeric"
      pattern="[0-9]*"
      parse={sanitizeDniInput}
      onKeyDown={handleDniKeyDown}
      label={intl.formatMessage({ id: identityMessages.label })}
      placeholder={intl.formatMessage({ id: identityMessages.placeholder })}
      validate={dniValidation}
    />
  );
};

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        authInfo,
        idp,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailInvalid',
        })
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailPlaceholder',
                })}
                initialValue={email}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.firstName` : 'firstName'}
                  name="firstName"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNamePlaceholder',
                  })}
                  initialValue={firstName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lastName` : 'lastName'}
                  name="lastName"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNamePlaceholder',
                  })}
                  initialValue={lastName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              <UserFieldPhoneNumber
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <CuitMaybe formId={formId} userType={userType} intl={intl} className={css.row} />
              <DniMaybe formId={formId} userType={userType} intl={intl} className={css.row} />
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the confirm signup form, which is used with SSO authentication.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const ConfirmSignupForm = props => {
  const intl = useIntl();
  return <ConfirmSignupFormComponent {...props} intl={intl} />;
};

export default ConfirmSignupForm;
